import styled from 'styled-components';

import type {
  PartnerShopPlannerContent,
  PartnerShopPlannerContentPhoto,
  PartnerShopPlannerPhoto,
} from '@/api/partnerShops';

interface PlannerContentSectionProps {
  content: PartnerShopPlannerContent;
}

function isPlannerPhoto(
  value: PartnerShopPlannerContentPhoto
): value is PartnerShopPlannerPhoto {
  return 'imageUrl' in value && Boolean(value.imageUrl);
}

function PhotoGrid({ photos }: { photos: PartnerShopPlannerContentPhoto[] }) {
  const resolvedPhotos = photos.filter(isPlannerPhoto);

  if (resolvedPhotos.length === 0) return null;

  return (
    <PhotoList>
      {resolvedPhotos.map((photo) => (
        <PhotoFigure key={photo.photoKey}>
          <img
            src={photo.imageUrl}
            alt={
              photo.alt ||
              photo.caption ||
              photo.imageSummary ||
              photo.imageCategoryName ||
              '플래너 큐레이션 사진'
            }
            loading="lazy"
            referrerPolicy="no-referrer"
          />
        </PhotoFigure>
      ))}
    </PhotoList>
  );
}

export default function PlannerContentSection({
  content,
}: PlannerContentSectionProps) {
  const sections = content.sections || [];
  const heroPhotos = content.heroPhotos || [];

  return (
    <PreviewSection>
      <Badge>
        <BadgeDot />
        다이렉트웨딩 후기 기반 검증 업체
      </Badge>

      <Headline>{content.headline}</Headline>

      {content.introduction && (
        <Introduction>{content.introduction}</Introduction>
      )}

      <PhotoGrid photos={heroPhotos} />

      {sections.map((section, index) => (
        <ContentBlock key={`${section.title}-${index}`}>
          <SectionTitle>{section.title}</SectionTitle>
          <SectionBody>{section.body}</SectionBody>
          <PhotoGrid photos={section.photos || []} />
        </ContentBlock>
      ))}

      {content.plannerQuote?.text && (
        <PlannerQuote>
          <p>&quot;{content.plannerQuote.text}&quot;</p>
          <footer>다이렉트 카페 실제 후기 중에서</footer>
        </PlannerQuote>
      )}
    </PreviewSection>
  );
}

const PreviewSection = styled.section`
  width: 100%;
  max-width: 720px;
  margin: 0 auto;
  padding: 36px 40px 56px;
  border-radius: 18px;
  background: #fff;
  color: #17171a;
  box-shadow: 0 8px 30px rgba(23, 23, 26, 0.06);
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 16px;
  padding: 5px 12px;
  border-radius: 999px;
  background: #17171a;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
`;

const BadgeDot = styled.span`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #d6927a;
`;

const Headline = styled.h2`
  margin: 0 0 16px;
  font-size: 26px;
  line-height: 1.4;
  letter-spacing: -0.023em;
`;

const Introduction = styled.p`
  margin: 0 0 24px;
  color: rgba(46, 47, 51, 0.88);
  font-size: 15.5px;
  line-height: 1.8;
`;

const PhotoList = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin: 24px 0;
`;

const PhotoFigure = styled.figure`
  aspect-ratio: 4 / 3;
  margin: 0;
  overflow: hidden;
  border-radius: 16px;
  background: #f4f4f5;

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ContentBlock = styled.section`
  margin-top: 28px;
`;

const SectionTitle = styled.h3`
  margin: 0 0 10px;
  font-size: 19px;
  letter-spacing: -0.012em;
`;

const SectionBody = styled.p`
  margin: 0 0 24px;
  color: rgba(46, 47, 51, 0.88);
  font-size: 15.5px;
  line-height: 1.8;
  white-space: pre-line;
`;

const PlannerQuote = styled.blockquote`
  margin: 28px 0 0;
  padding: 20px 24px;
  border-radius: 12px;
  background: #fbf1ed;

  p {
    margin: 0;
    font-family: serif;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.9;
    letter-spacing: -0.02em;
  }

  footer {
    margin-top: 6px;
    color: rgba(55, 56, 60, 0.4);
    font-size: 12px;
  }
`;
